// Homepage Shop the Tackroom grid, quick shop and bag drawer on the store's cart (Shopify Ajax Cart API).
// Prices come in the shopper's currency (Shopify Markets); whole amounts drop cents as the store prints them ($43).
const currency = window.Shopify?.currency?.active || 'USD';
const moneyFormat = options => {
  try { return new Intl.NumberFormat('en-US', { style: 'currency', currency, currencyDisplay: 'narrowSymbol', ...options }); }
  catch { return new Intl.NumberFormat('en-US', { style: 'currency', currency, ...options }); } // Safari before 14.1
};
const wholeDollars = moneyFormat({ minimumFractionDigits: 0, maximumFractionDigits: 0 });
const withCents = moneyFormat({});
const amount = cents => (cents % 100 === 0 ? wholeDollars : withCents).format(cents / 100);
const escapeHTML = value => String(value).replace(/[&<>"']/g, character => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[character]));
const categoryTitles = { all: 'SHOP THE TACKROOM', tees: 'Shop the tees', hats: 'Shop the hats', men: "Shop men's", women: "Shop women's" };
const get = id => document.getElementById(id);
const cartUrl = path => `${window.Shopify?.routes?.root || '/'}${path}`;
// a shopper-usage count, sent by stally-rd.js
const trackUse = name => document.dispatchEvent(new CustomEvent('stally:track', { detail: name }));

// Shopify's image CDN resizes by its width parameter; the section prints 1200w photos that cards show far smaller.
const resized = (src, width) => {
  try {
    const url = new URL(src, location.href);
    url.searchParams.set('width', width);
    return url.href;
  } catch {
    return src;
  }
};
// Cards are a quarter of the grid (half on phones and 750-879px), and their crops zoom photos up to 1.4x.
const CARD_WIDTHS = [400, 600, 800, 1000, 1200];
const CARD_SIZES = '(max-width: 650px) 60vw, (min-width: 750px) and (max-width: 879px) 60vw, 30vw';
const cardSrcset = src => CARD_WIDTHS.map(width => `${resized(src, width)} ${width}w`).join(', ');
// where the pointer can't hover, sections/stally-rd-rotation.liquid hides a card's hover photo and it never loads
const hoverPhotoHidden = matchMedia('(hover: none)');

// Source-photo bounds keep each hat 86% of the frame width, centered at 55% height.
function photoFrameStyle(bounds) {
  if (!bounds) return '';
  const [left, top, right, bottom] = bounds;
  const width = 86 / (right - left);
  return `--hat-width:${width}%;--hat-left:${7 - left * width}%;--hat-center:${(top + bottom) * 50}%`;
}


const normalize = value => String(value).trim().toLocaleLowerCase('en-US');
function filterProducts(products, { type = 'all', audience = 'all', query = '' } = {}) {
  const search = normalize(query);
  return products.filter(product =>
    (type === 'all' || product.type === type) && (audience === 'all' || product.audiences.includes(audience)) &&
    (!search || normalize([product.name, product.type, ...product.colors.map(color => color.name)].join(' ')).includes(search)));
}

// Search and bag links open their dialogs here; menu links go to their category pages, not homepage anchors.
function homepageLinkAction(link) {
  const url = new URL(link.href, location.href);
  if (url.origin !== location.origin) return null;
  const path = url.pathname.replace(/\/$/, '');
  if (path === '/search') return { open: 'search-dialog' };
  if (path === '/cart') return { open: 'bag-dialog' };
  return null;
}

// The JSON sections/stally-rd-rotation.liquid prints from editor settings: a bad entry is dropped, not fatal.
function usableProducts(data) {
  const text = value => typeof value === 'string' && value.trim().length > 0;
  const bounds = value => Array.isArray(value) && value.length === 4 && value.every(n => Number.isFinite(n) && n >= 0 && n <= 1) && value[2] > value[0] && value[3] > value[1];
  // a crop height ends up in a style attribute, so only a number gets through
  const photo = card => card && text(card.src) ? { ...card, height: Number.isFinite(card.height) ? card.height : 100 } : null;
  const dimension = value => Number.isSafeInteger(value) && value > 0 ? value : null;
  // same-site path only (it lands in an href); URL parsers drop tabs and newlines, so '/\t/x' would become '//x'
  const path = value => text(value) && value.startsWith('/') && !value.startsWith('//') && !/[\\\s\u0000-\u001f]/.test(value) ? value : null;
  return (Array.isArray(data) ? data : []).map(product => ({
    ...product,
    fit: text(product.fit) ? product.fit.trim() : '',
    colors: (product.colors || []).filter(color => text(color.name) && text(color.image) && color.sizes?.length)
      .map(color => ({ ...color, url: path(color.url) || path(product.url) || '/collections/all', imageWidth: dimension(color.imageWidth), imageHeight: dimension(color.imageHeight), model: text(color.model) ? color.model.trim() : '',
        cardImage: photo(color.cardImage), cardAlternate: photo(color.cardAlternate), photoBounds: bounds(color.photoBounds) ? color.photoBounds : null }))
  })).filter(product => text(product.name) && Number.isSafeInteger(product.price) && product.colors.length);
}

function openDialog(id) {
  const dialog = get(id);
  for (const nav of document.querySelectorAll('.rd .desktop-nav > details[open]')) {
    nav.querySelector('summary').focus();
    nav.open = false;
  }
  document.querySelectorAll('.rd dialog[open]').forEach(open => { if (open !== dialog) open.close(); });
  if (!dialog.open) dialog.showModal();
}

function initializeRotation() {
  const grid = get('product-grid');
  if (!grid) return null;
  let products;
  try {
    products = usableProducts(JSON.parse(get('rotation-data').textContent));
  } catch (error) {
    console.warn('Shop the Tackroom: product data unreadable.', error);
    products = [];
  }
  let quick = null;
  const requestedCategory = new URLSearchParams(location.search).get('category');
  let category = Object.hasOwn(categoryTitles, requestedCategory) ? requestedCategory : 'all';
  const selectedColors = new Map();
  const colorRequests = new Map();
  const empty = get('empty-products');
  const emptyMessage = empty.querySelector('p');
  const productById = id => products.find(product => String(product.id) === id);
  const swatches = (product, selected, attribute) => product.colors.map((color, index) =>
    `<button type="button" class="swatch" ${attribute}="${index}" aria-label="${escapeHTML(color.name)}" aria-pressed="${index === selected}"><span class="visually-hidden">${escapeHTML(color.name)}</span></button>`
  ).join('');
  const paintSwatches = (container, colors) => {
    container.querySelectorAll('.swatch').forEach((button, index) => button.style.setProperty('--swatch-color', colors[index].hex));
  };

  // Card-only crops exclude baked-in labels; full source photos remain in quick shop.
  const cardPhotosOf = color => {
    const front = color.cardImage;
    const alternate = color.cardAlternate || front;
    return { front, alternate, frontSrc: front?.src || color.image, alternateSrc: alternate?.src || color.alternate || color.image };
  };
  // two cards of one product (a men's and a women's) differ only in their photos, so the photo's wearer is said too
  const wornBy = color => ({ men: ', worn by a man', women: ', worn by a woman' })[color.wornBy] || '';
  const cardVisual = (product, color) => {
    const { front, alternate, frontSrc, alternateSrc } = cardPhotosOf(color);
    return `<img class="product-photo" src="${escapeHTML(frontSrc)}" srcset="${escapeHTML(cardSrcset(frontSrc))}" sizes="${CARD_SIZES}" alt="${escapeHTML(product.name)} in ${escapeHTML(color.name)}${wornBy(color)}" style="--photo-height:${front?.height || 100}%;${photoFrameStyle(color.photoBounds)}" width="1200" height="1500" loading="lazy" decoding="async">
      <img class="product-alternate" src="${escapeHTML(alternateSrc)}" srcset="${escapeHTML(cardSrcset(alternateSrc))}" sizes="${CARD_SIZES}" alt="" aria-hidden="true" style="--photo-height:${alternate?.height || 100}%;${photoFrameStyle(color.photoBounds)}" width="1200" height="1500" loading="lazy" decoding="async">`;
  };
  // The model's size for the card photo: its card caption, else the size from the quick shop photo it shows. It is told
  // in an ⓘ over the photo's corner (Jasen, Oct 29), beside the photo link: pointing at the ⓘ leaves the link, so the
  // front photo, the one it describes, shows.
  const modelOf = color => (color.cardImage ? color.cardImage.caption : color.model) || '';
  // the card's ⓘ: a copy of quick shop's (snippets/stally-rd-model-info.liquid), whose behaviour is in stally-rd.js
  const cardTip = (product, color) => {
    const text = modelOf(color), id = `card-model-${product.id}`;
    const tip = get('quick-model')?.closest('.rd-tip')?.cloneNode(true);
    if (!tip || !text) return null;
    tip.hidden = false;
    tip.querySelector('.rd-tip-btn').setAttribute('aria-describedby', id);
    Object.assign(tip.querySelector('.rd-tip-text'), { id, textContent: text, hidden: true });
    return tip;
  };
  const quickShopLabel = (product, color) => `Quick shop ${product.name}, ${color.name}${wornBy(color)}${modelOf(color) ? `. ${modelOf(color)}` : ''}`;
  // Link the color's page at the chosen size; with none chosen, ?choose_size leaves it to the shopper (stally-rd-product.js).
  const productHref = (color, sizeName = null) => {
    const chosen = color.sizes.find(choice => choice.name === sizeName && choice.available);
    const size = chosen || color.sizes.find(choice => choice.available) || color.sizes[0];
    if (!Number.isSafeInteger(size?.id)) return color.url;
    // a sold-out color opens on Sold out and Notify me
    return `${color.url}?variant=${size.id}${!chosen && color.sizes.length > 1 && size.available ? '&choose_size' : ''}`;
  };

  function renderGrid() {
    const visible = filterProducts(products, ['men', 'women'].includes(category) ? { audience: category } : { type: category });
    get('rotation-title').textContent = categoryTitles[category];
    grid.innerHTML = visible.map(product => {
      const index = selectedColors.get(String(product.id)) || 0;
      const color = product.colors[index];
      selectedColors.set(String(product.id), index);
      // The photo link leaves the tab order: the title is the same link for a keyboard.
      return `<article class="rd-product-card" data-product="${escapeHTML(product.id)}" data-product-type="${escapeHTML(product.type)}">
        <a class="product-visual" href="${escapeHTML(productHref(color))}" tabindex="-1">
          ${cardVisual(product, color)}
        </a>${cardTip(product, color)?.outerHTML || ''}
        <div class="product-meta"><a class="product-title" href="${escapeHTML(productHref(color))}">${escapeHTML(product.name)}</a><span class="rd-product-price">${amount(product.price)}</span></div>
        <p class="color-label"><span class="color-name">${escapeHTML(color.name)}</span>${product.fit ? ` · ${escapeHTML(product.fit)}` : ''}</p>
        <div class="card-actions"><div class="rd-swatches" role="group" aria-label="${escapeHTML(product.name)} colors">${swatches(product, index, 'data-product-color')}</div><button type="button" class="quick-shop-button" data-quick-id="${escapeHTML(product.id)}" aria-label="${escapeHTML(quickShopLabel(product, color))}">Quick shop</button></div>
      </article>`;
    }).join('');
    grid.querySelectorAll('[data-product]').forEach(card => paintSwatches(card, productById(card.dataset.product).colors));
    grid.setAttribute('aria-busy', 'false');
    empty.hidden = visible.length > 0;
    emptyMessage.textContent = products.length ? 'No pieces match this combination.' : 'The collection could not load. Please refresh the page.';
    get('product-count').textContent = `${visible.length} ${visible.length === 1 ? 'piece' : 'pieces'}`;
    document.querySelectorAll('[data-filter]').forEach(control => {
      const active = control.dataset.filter === category;
      if (control.tagName === 'BUTTON') control.setAttribute('aria-pressed', String(active));
      else if (active) control.setAttribute('aria-current', 'true');
      else control.removeAttribute('aria-current');
    });
  }

  async function selectProductColor(product, index, selection = null) {
    const color = product.colors[index];
    if (!color) return;
    const id = String(product.id);
    const request = {};
    colorRequests.set(id, request);
    const current = () => colorRequests.get(id) === request && (!selection || (quick === selection && get('quick-dialog').open));
    try {
      // the photos cardVisual() draws, with its srcset (and quick shop's full photo), so nothing loads twice; the hover
      // photo only where it can show, so a phone's color tap doesn't wait for a photo it never shows
      const { frontSrc, alternateSrc } = cardPhotosOf(color);
      const sources = (hoverPhotoHidden.matches ? [frontSrc] : [frontSrc, alternateSrc]).map(src => ({ src, srcset: cardSrcset(src) }));
      if (selection) sources.push({ src: color.image, srcset: '' });
      const unique = [...new Map(sources.map(source => [`${source.src} ${source.srcset}`, source])).values()];
      await Promise.all(unique.map(({ src, srcset }) => {
        const image = new Image();
        if (srcset) {
          image.sizes = CARD_SIZES;
          image.srcset = srcset;
        }
        image.src = src;
        return image.decode();
      }));
    } catch {
      if (current()) get(selection ? 'quick-status' : 'product-count').textContent = 'This color photo could not load. Please try again.';
      return;
    }
    if (!current()) return;
    selectedColors.set(id, index);
    const card = [...grid.querySelectorAll('[data-product]')].find(item => item.dataset.product === id);
    if (card) {
      const visual = card.querySelector('.product-visual');
      visual.innerHTML = cardVisual(product, color);
      card.querySelector('.rd-tip')?.remove();
      const tip = cardTip(product, color);
      if (tip) visual.after(tip);
      visual.href = productHref(color);
      card.querySelector('.quick-shop-button').setAttribute('aria-label', quickShopLabel(product, color));
      card.querySelector('.product-title').href = productHref(color);
      card.querySelector('.color-name').textContent = color.name;
      card.querySelectorAll('.swatch').forEach((button, position) => button.setAttribute('aria-pressed', String(position === index)));
    }
    if (selection) {
      quick.color = index;
      quick.size = color.sizes.some(size => size.name === quick.size && size.available) ? quick.size : defaultSize(color);
      renderQuickSelection();
    }
    get('product-count').textContent = `${product.name}, ${color.name} selected.`;
  }

  function defaultSize(color) {
    return color.sizes.length === 1 && /^(?:one[ -]?size|os|osfa|adjustable)$/i.test(color.sizes[0].name) && color.sizes[0].available ? color.sizes[0].name : null;
  }

  function renderQuickSelection() {
    const color = quick.product.colors[quick.color];
    get('quick-image').src = color.image;
    get('quick-image').alt = `${quick.product.name} in ${color.name}${wornBy(color)}`;
    get('quick-image').dataset.productType = quick.product.type;
    get('quick-image').style.cssText = photoFrameStyle(color.photoBounds);
    // the photo shows whole at its own shape, so its size comes before it loads (no jump when the color changes)
    get('quick-image').width = color.imageWidth || 1200;
    get('quick-image').height = color.imageHeight || 1500;
    // the model's size ⓘ for this photo (snippets/stally-rd-model-info.liquid)
    get('quick-model').textContent = color.model;
    get('quick-model').closest('.rd-tip').hidden = !color.model;
    get('quick-color-name').textContent = color.name;
    get('quick-swatches').querySelectorAll('.swatch').forEach((button, index) => button.setAttribute('aria-pressed', String(index === quick.color)));
    get('quick-sizes').innerHTML = color.sizes.map((size, index) => `<button type="button" class="size-choice" data-size-choice="${index}" aria-pressed="${size.name === quick.size}" aria-label="${escapeHTML(size.name)}${size.available ? '' : ', sold out'}"${size.available ? '' : ' disabled'}>${escapeHTML(size.name)}</button>`).join('');
    updateQuickStatus();
  }

  function updateQuickStatus(message) {
    const color = quick.product.colors[quick.color];
    const available = color.sizes.some(size => size.available);
    const selected = color.sizes.find(size => size.name === quick.size && size.available);
    get('quick-sizes').querySelectorAll('[data-size-choice]').forEach(button => button.setAttribute('aria-pressed', String(color.sizes[Number(button.dataset.sizeChoice)].name === quick.size)));
    get('quick-status').textContent = message || (!available ? 'This color is currently sold out.' : selected ? `${selected.name} selected.` : 'Select a size to add this piece.');
    get('add-to-bag').disabled = !selected || quick.adding;
    get('quick-title-link').href = productHref(color, quick.size);
    get('add-to-bag').textContent = !available ? 'Sold out' : selected ? (typeof color.preorder === 'string' ? 'Pre-order' : 'Add to bag') : 'Select a size';
    // a pre-order's ship line and a final-sale item's line, as on the product page; a hidden line must not describe
    // the button
    const shipLine = get('quick-preorder');
    shipLine.textContent = color.preorder || '';
    shipLine.hidden = !color.preorder || !available;
    const finalLine = get('quick-final-sale');
    if (finalLine) finalLine.hidden = !color.finalSale;
    const described = [finalLine, shipLine].filter(line => line && !line.hidden).map(line => line.id).join(' ');
    if (described) get('add-to-bag').setAttribute('aria-describedby', described);
    else get('add-to-bag').removeAttribute('aria-describedby');
  }

  function openQuick(id, colorIndex) {
    const product = productById(id);
    if (!product) return;
    colorRequests.delete(id);
    const color = colorIndex ?? selectedColors.get(id) ?? 0;
    quick = { product, color, size: defaultSize(product.colors[color]), adding: false };
    get('quick-title-link').textContent = product.name;
    get('quick-price').textContent = amount(product.price);
    get('quick-fit').textContent = product.fit;
    get('quick-fit').hidden = !product.fit;
    get('quick-description').textContent = product.description;
    get('quick-swatches').innerHTML = swatches(product, color, 'data-quick-color');
    paintSwatches(get('quick-swatches'), product.colors);
    renderQuickSelection();
    openDialog('quick-dialog');
    trackUse('stally_quick_open');
  }

  // Not Concept's cartUpdate event: its cart drawer throws without pre-rendered sections in it.
  const publishCart = cart => document.dispatchEvent(new CustomEvent('stally:cart', { detail: cart }));
  async function cartRequest(path, body) {
    const response = await fetch(cartUrl(path), body ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) } : { headers: { Accept: 'application/json' } });
    const json = await response.json();
    if (!response.ok) throw new Error(json.description || json.message || `Cart request failed: ${response.status}`);
    return json;
  }

  // a line's properties (a pre-order's ship line), skipped as the cart page skips them: blank, or named with a leading _
  const properties = item => Object.entries(item.properties || {})
    .filter(([name, value]) => value && !name.startsWith('_'))
    .map(([name, value]) => `<p>${escapeHTML(name)}: ${escapeHTML(value)}</p>`).join('');

  function renderBag(cart) {
    get('bag-empty').hidden = cart.item_count > 0;
    get('bag-total').textContent = amount(cart.total_price);
    get('bag-summary').hidden = cart.item_count === 0;
    get('bag-items').innerHTML = cart.items.map(item => {
      const variant = item.variant_title || 'One size';
      return `<article class="bag-item">
      <img class="bag-photo" src="${escapeHTML(item.image ? resized(item.image, 300) : '')}" alt="${escapeHTML(item.product_title)}, ${escapeHTML(variant)}" width="1200" height="1500">
      <div class="bag-copy"><h3>${escapeHTML(item.product_title)}</h3><p>${escapeHTML(variant)}</p>${properties(item)}<p>${amount(item.final_price)}</p>
        <div class="quantity-controls"><button type="button" data-cart-action="decrease" data-cart-key="${escapeHTML(item.key)}" aria-label="Decrease quantity of ${escapeHTML(item.product_title)}, ${escapeHTML(variant)}">−</button><span aria-label="Quantity ${item.quantity}">${item.quantity}</span><button type="button" data-cart-action="increase" data-cart-key="${escapeHTML(item.key)}" aria-label="Increase quantity of ${escapeHTML(item.product_title)}, ${escapeHTML(variant)}">+</button></div>
        <button type="button" class="remove-item" data-cart-action="remove" data-cart-key="${escapeHTML(item.key)}" aria-label="Remove ${escapeHTML(item.product_title)}, ${escapeHTML(variant)}">Remove</button>
      </div><strong>${amount(item.final_line_price)}</strong>
    </article>`;
    }).join('');
  }

  async function addToBag() {
    if (!quick || quick.adding) return;
    const color = quick.product.colors[quick.color];
    const size = color.sizes.find(choice => choice.name === quick.size && choice.available);
    if (!size) return;
    const selection = quick;
    selection.adding = true;
    get('bag-status').textContent = '';
    updateQuickStatus('Adding to your bag…');
    try {
      // the product page's form posts the same Pre-order property
      await cartRequest('cart/add.js', { items: [{ id: size.id, quantity: 1, ...(color.preorder ? { properties: { 'Pre-order': color.preorder } } : {}) }] });
      const cart = await cartRequest('cart.js');
      selection.adding = false;
      publishCart(cart);
      renderBag(cart);
      trackUse('stally_quick_add');
      if (quick === selection) openDialog('bag-dialog');
    } catch (error) {
      selection.adding = false;
      if (quick === selection) updateQuickStatus(error.message);
    }
  }

  let bagBusy = false;
  async function changeLine(key, action) {
    if (bagBusy) return;
    bagBusy = true;
    get('bag-status').textContent = '';
    try {
      const item = (await cartRequest('cart.js')).items.find(row => row.key === key);
      if (!item) return;
      const quantity = action === 'remove' ? 0 : item.quantity + (action === 'increase' ? 1 : -1);
      const cart = await cartRequest('cart/change.js', { id: key, quantity: Math.max(0, quantity) });
      publishCart(cart);
      renderBag(cart);
      const message = quantity > 0 ? `${item.product_title}: quantity ${quantity}. Subtotal ${amount(cart.total_price)}` : `${item.product_title} removed from your bag.`;
      get('bag-live').textContent = '';
      requestAnimationFrame(() => { get('bag-live').textContent = message; });
      const next = [...get('bag-items').querySelectorAll('[data-cart-action]')].find(button => button.dataset.cartKey === key && button.dataset.cartAction === action);
      (next || get('bag-items').querySelector('button') || get('bag-dialog').querySelector('[data-close]')).focus();
    } catch (error) {
      get('bag-status').textContent = error.message; // role=alert: shown and announced
      console.warn('Bag update failed.', error);
      // Shopify clamps the line to what's in stock even when it refuses the full change; show what it kept
      cartRequest('cart.js').then(cart => {
        publishCart(cart);
        renderBag(cart);
        const again = [...get('bag-items').querySelectorAll('[data-cart-action]')].find(button => button.dataset.cartKey === key && button.dataset.cartAction === action);
        (again || get('bag-items').querySelector('button') || get('bag-dialog').querySelector('[data-close]')).focus();
      }).catch(() => {});
    } finally {
      bagBusy = false;
    }
  }

  function renderSearch() {
    const query = get('search-input').value;
    const matches = filterProducts(products, { query });
    get('search-status').textContent = matches.length ? `${matches.length} ${matches.length === 1 ? 'piece' : 'pieces'}${query.trim() ? ` for “${query.trim()}”` : ' to explore'}.` : 'No pieces found. Try a name or a color.';
    get('search-results').innerHTML = matches.map(product => {
      const color = product.colors[0];
      const colorCount = `${product.colors.length} ${product.colors.length === 1 ? 'color' : 'colors'}`;
      // the photo is decorative, so a men's and a women's card of one product say their wearer in the row's name: a label
      // (a visually hidden span sits outside the text flow, and Chromium set it off with a space, "Hoodie , worn"), the
      // visible text first and in order (WCAG 2.5.3), the wearer last
      const label = `${product.name}, ${colorCount}, ${amount(product.price)}${wornBy(color)}`;
      return `<a class="search-result" href="${escapeHTML(productHref(color))}" aria-label="${escapeHTML(label)}"><img class="search-result-image" src="${escapeHTML(resized(color.image, 200))}" alt="" width="1200" height="1500" loading="lazy"><span class="search-result-copy"><strong>${escapeHTML(product.name)}</strong><span>${colorCount}</span></span><span>${amount(product.price)}</span></a>`;
    }).join('');
    const all = get('search-all');
    if (all) {
      const url = new URL(all.getAttribute('href'), location.href);
      url.search = '';
      if (query.trim()) url.searchParams.set('q', query.trim());
      all.href = url;
      all.parentElement.hidden = !query.trim();
    }
  }
  get('search-input')?.addEventListener('input', renderSearch);

  async function openBag() {
    renderBag(await cartRequest('cart.js'));
    openDialog('bag-dialog');
  }

  function followHomepageLink(event, link) {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return false;
    if (!link.closest('.shop-header, #menu-dialog, .footer-edit')) return false;
    const action = homepageLinkAction(link);
    if (!action) return false;
    event.preventDefault();
    link.closest('dialog')?.close();
    if (action.open === 'search-dialog') {
      renderSearch();
      openDialog('search-dialog');
    } else if (action.open === 'bag-dialog') {
      openBag().catch(error => { console.warn('Bag unavailable.', error); location.href = link.href; });
    }
    return true;
  }

  const onClick = event => {
    if (!(event.target instanceof Element)) return;
    const link = event.target.closest('a[href]');
    if (link && followHomepageLink(event, link)) return;
    const control = event.target.closest('button, a');
    if (!control) return;
    if (Object.hasOwn(categoryTitles, control.dataset.filter)) {
      category = control.dataset.filter;
      renderGrid();
      control.closest('dialog')?.close();
    }
    if (control.hasAttribute('data-reset-filters')) {
      category = 'all';
      renderGrid();
      // the button just hid itself; keep focus where the products start
      get('rotation-title').focus({ preventScroll: true });
    }
    if (control.dataset.quickId) openQuick(control.dataset.quickId);
    if (control.hasAttribute('data-product-color')) selectProductColor(productById(control.closest('[data-product]').dataset.product), Number(control.dataset.productColor));
    if (control.hasAttribute('data-quick-color') && quick) selectProductColor(quick.product, Number(control.dataset.quickColor), quick);
    // counted at the tap; the color's photos load after
    if (control.matches('[data-product-color], [data-quick-color]')) trackUse('stally_swatch_tap');
    if (control.hasAttribute('data-size-choice') && quick) {
      const size = quick.product.colors[quick.color].sizes[Number(control.dataset.sizeChoice)];
      if (size.available) { quick.size = size.name; updateQuickStatus(); }
    }
    if (control.id === 'add-to-bag') addToBag();
    if (control.dataset.cartAction) changeLine(control.dataset.cartKey, control.dataset.cartAction);
  };

  renderGrid();
  return onClick;
}

// The theme editor re-renders a section without re-running scripts: setup re-runs, and one listener calls the latest.
let rotationClick = initializeRotation();
document.addEventListener('click', event => rotationClick?.(event));
document.addEventListener('shopify:section:load', event => {
  if (event.target.querySelector('#product-grid')) rotationClick = initializeRotation();
});
